import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Table, Alert, Badge, Button, Card, ButtonGroup } from 'react-bootstrap';
import { FaArrowRight, FaRedo, FaTrash, FaCheck, FaExclamationTriangle, FaUsers, FaInfoCircle } from 'react-icons/fa';

const StudentDecisionStep = ({ workflowData, setWorkflowData }) => {
  const [error, setError] = useState("");
  const [bulkAction, setBulkAction] = useState("");
  const [bulkTargetClass, setBulkTargetClass] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkRepeatYear, setBulkRepeatYear] = useState("");

  // Generate academic year options
  const generateAcademicYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    
    for (let i = currentYear - 2; i <= currentYear + 3; i++) {
      const academicYear = `${i}-${(i + 1).toString().slice(-2)}`;
      years.push({
        value: academicYear,
        label: `${i}/${(i + 1).toString().slice(-2)} Academic Year`
      });
    }
    
    return years.reverse();
  };

  // Set default repeat year on component mount
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    
    let academicYear;
    if (currentMonth >= 8) { // September onwards
      academicYear = `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
    } else { // January to August
      academicYear = `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
    }
    
    setBulkRepeatYear(academicYear);
  }, []);

  const updateStudentDecision = (studentId, field, value) => {
    setWorkflowData(prev => ({
      ...prev,
      studentDecisions: {
        ...prev.studentDecisions,
        [studentId]: {
          ...prev.studentDecisions[studentId],
          [field]: value
        }
      }
    }));
  };

  const handleActionChange = (studentId, action) => {
    const decision = {
      action: action,
      targetClassId: action === 'move' ? '' : null,
      repeatYear: action === 'repeat' ? bulkRepeatYear : null,
      reason: action === 'move' ? null : ''
    };

    setWorkflowData(prev => ({
      ...prev,
      studentDecisions: {
        ...prev.studentDecisions,
        [studentId]: decision
      }
    }));
  };

  const applyBulkAction = () => {
    if (!bulkAction) {
      setError("Please select a bulk action.");
      return;
    }

    const studentsToProcess = workflowData.populationMethod === 'new' 
      ? workflowData.newStudents?.filter(s => s.isValid) || []
      : workflowData.selectedStudents || [];

    if (studentsToProcess.length === 0) {
      setError("No students to apply bulk action to.");
      return;
    }

    // Validate bulk action requirements
    if (bulkAction === 'move' && !bulkTargetClass) {
      setError("Please select a target class for bulk move action.");
      return;
    }

    if (bulkAction === 'repeat' && (!bulkReason.trim() || !bulkRepeatYear)) {
      setError("Please provide a reason and academic year for bulk repeat action.");
      return;
    }

    if (bulkAction === 'remove' && !bulkReason.trim()) {
      setError("Please provide a reason for bulk remove action.");
      return;
    }

    // Apply bulk action to all students
    const newDecisions = { ...workflowData.studentDecisions };
    
    studentsToProcess.forEach(student => {
      const studentId = student.id;
      newDecisions[studentId] = {
        action: bulkAction,
        targetClassId: bulkAction === 'move' ? bulkTargetClass : null,
        repeatYear: bulkAction === 'repeat' ? bulkRepeatYear : null,
        reason: bulkAction === 'move' ? null : bulkReason
      };
    });

    setWorkflowData(prev => ({
      ...prev,
      studentDecisions: newDecisions
    }));

    setError("");
  };

  const clearBulkAction = () => {
    setBulkAction("");
    setBulkTargetClass("");
    setBulkReason("");
  };

  const getDecisionSummary = () => {
    const decisions = workflowData.studentDecisions || {};
    const summary = {
      move: 0,
      repeat: 0,
      remove: 0,
      total: Object.keys(decisions).length
    };

    Object.values(decisions).forEach(decision => {
      if (decision.action) {
        summary[decision.action]++;
      }
    });

    return summary;
  };

  const isDecisionValid = (decision) => {
    if (!decision || !decision.action) return false;
    
    switch (decision.action) {
      case 'move':
        return !!decision.targetClassId;
      case 'repeat':
        return !!decision.repeatYear && !!decision.reason?.trim();
      case 'remove':
        return !!decision.reason?.trim();
      default:
        return false;
    }
  };

  // Handle simple transitions - auto-complete with confirmation
  if (workflowData.populationMethod === 'simple') {
    const classMappings = workflowData.classMappings || {};
    const mappingCount = Object.keys(classMappings).length;
    
    return (
      <div>
        <h5>Simple Grade Progression - Ready for Confirmation</h5>
        <p className="text-muted">Students will automatically move from their current classes to the mapped target classes.</p>
        
        <Alert variant="info">
          <FaInfoCircle className="me-2" />
          For simple grade progressions, student decisions are automatic based on the class mappings you created. 
          All students from mapped source classes will move to their corresponding target classes.
        </Alert>

        <Card>
          <Card.Header>
            <h6 className="mb-0">Transition Summary</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Badge bg="primary" className="me-2">Class Mappings: {mappingCount}</Badge>
                  <Badge bg="success">Ready for Execution</Badge>
                </div>
                
                <div className="text-muted">
                  <p><strong>What will happen:</strong></p>
                  <ul>
                    <li>Students will be moved from source classes to target classes based on your mappings</li>
                    <li>All moves will be processed as "promotion to next grade"</li>
                    <li>No manual student decisions required</li>
                  </ul>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded">
                  <h6>Next Step:</h6>
                  <p className="mb-0 text-muted">
                    Click "Next" to review the final confirmation and execute the class transitions.
                  </p>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Render for new students (assign to classes)
  if (workflowData.populationMethod === 'new') {
    const newStudents = workflowData.newStudents?.filter(s => s.isValid) || [];
    const createdClasses = workflowData.createdClasses || [];
    const summary = getDecisionSummary();

    return (
      <div>
        <h5>Assign New Students to Classes</h5>
        <p className="text-muted">Assign each new student to one of the created classes.</p>
        
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Summary */}
        <div className="mb-4">
          <h6>Assignment Summary</h6>
          <div className="d-flex gap-3">
            <Badge bg="primary">Total Students: {newStudents.length}</Badge>
            <Badge bg="success">Assigned: {summary.total}</Badge>
            <Badge bg="warning">Remaining: {newStudents.length - summary.total}</Badge>
          </div>
        </div>

        {/* Bulk Assignment */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">
              <FaUsers className="me-2" />
              Bulk Assignment
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Assign All Students To:</Form.Label>
                  <Form.Select
                    value={bulkTargetClass}
                    onChange={(e) => setBulkTargetClass(e.target.value)}
                  >
                    <option value="">Select Class</option>
                    {createdClasses.map(classItem => (
                      <option key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Button 
                  variant="primary" 
                  onClick={() => {
                    if (!bulkTargetClass) {
                      setError("Please select a target class for bulk assignment.");
                      return;
                    }
                    
                    const newStudents = workflowData.newStudents?.filter(s => s.isValid) || [];
                    const newDecisions = { ...workflowData.studentDecisions };
                    
                    newStudents.forEach(student => {
                      newDecisions[student.id] = {
                        action: 'move',
                        targetClassId: bulkTargetClass,
                        repeatYear: null,
                        reason: null
                      };
                    });

                    setWorkflowData(prev => ({
                      ...prev,
                      studentDecisions: newDecisions
                    }));

                    setError("");
                  }}
                  disabled={!bulkTargetClass}
                  className="w-100"
                >
                  Assign All to Selected Class
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Individual Assignments */}
        <div style={{ height: '400px', overflowY: 'auto' }}>
          {newStudents.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No valid students to assign
            </div>
          ) : (
            <Table striped bordered hover size="sm">
              <thead className="sticky-top bg-light">
                <tr>
                  <th>Student Name</th>
                  <th>Assign to Class</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {newStudents.map(student => {
                  const decision = workflowData.studentDecisions[student.id] || {};
                  const isValid = isDecisionValid(decision);
                  
                  return (
                    <tr key={student.id}>
                      <td>
                        <div className="fw-medium">{student.displayName}</div>
                        <small className="text-muted">
                          {student.sen && <Badge bg="info" className="me-1">SEN</Badge>}
                          {student.esl && <Badge bg="warning" className="me-1">ESL</Badge>}
                          {student.asd && <Badge bg="secondary" className="me-1">ASD</Badge>}
                        </small>
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={decision.targetClassId || ''}
                          onChange={(e) => {
                            const targetClassId = e.target.value;
                            setWorkflowData(prev => ({
                              ...prev,
                              studentDecisions: {
                                ...prev.studentDecisions,
                                [student.id]: {
                                  action: 'move',
                                  targetClassId: targetClassId,
                                  repeatYear: null,
                                  reason: null
                                }
                              }
                            }));
                          }}
                        >
                          <option value="">Select Class</option>
                          {createdClasses.map(classItem => (
                            <option key={classItem.id} value={classItem.id}>
                              {classItem.name}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        {isValid ? (
                          <Badge bg="success">
                            <FaCheck className="me-1" />
                            Assigned
                          </Badge>
                        ) : (
                          <Badge bg="warning">Pending</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    );
  }

  // Render for existing students (move/repeat/remove decisions) - 'complex' method
  const selectedStudents = workflowData.selectedStudents || [];
  const createdClasses = workflowData.createdClasses || [];
  const summary = getDecisionSummary();
  const academicYears = generateAcademicYearOptions();

  return (
    <div>
      <h5>Student Decisions</h5>
      <p className="text-muted">Decide what happens to each selected student: move to new class, repeat current grade, or remove from system.</p>
      
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Summary */}
      <div className="mb-4">
        <h6>Decision Summary</h6>
        <div className="d-flex gap-3">
          <Badge bg="primary">Total Students: {selectedStudents.length}</Badge>
          <Badge bg="success">Move: {summary.move}</Badge>
          <Badge bg="warning">Repeat: {summary.repeat}</Badge>
          <Badge bg="danger">Remove: {summary.remove}</Badge>
          <Badge bg="info">Decisions Made: {summary.total}</Badge>
        </div>
      </div>

      {/* Bulk Actions */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0">
            <FaUsers className="me-2" />
            Bulk Actions
          </h6>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Bulk Action:</Form.Label>
                <Form.Select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                >
                  <option value="">Select Action</option>
                  <option value="move">Move All</option>
                  <option value="repeat">Mark All as Repeating</option>
                  <option value="remove">Remove All</option>
                </Form.Select>
              </Form.Group>
            </Col>
            
            {bulkAction === 'move' && (
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Target Class:</Form.Label>
                  <Form.Select
                    value={bulkTargetClass}
                    onChange={(e) => setBulkTargetClass(e.target.value)}
                  >
                    <option value="">Select Class</option>
                    {createdClasses.map(classItem => (
                      <option key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            
            {bulkAction === 'repeat' && (
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Academic Year:</Form.Label>
                  <Form.Select
                    value={bulkRepeatYear}
                    onChange={(e) => setBulkRepeatYear(e.target.value)}
                  >
                    {academicYears.map(year => (
                      <option key={year.value} value={year.value}>
                        {year.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            
            {(bulkAction === 'repeat' || bulkAction === 'remove') && (
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Reason:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Reason required..."
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
          
          <div className="d-flex gap-2">
            <Button 
              variant="primary" 
              onClick={applyBulkAction}
              disabled={!bulkAction}
            >
              Apply to All Students
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={clearBulkAction}
            >
              Clear
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Individual Decisions */}
      <div style={{ height: '400px', overflowY: 'auto' }}>
        {selectedStudents.length === 0 ? (
          <div className="text-center py-4 text-muted">
            No students selected for processing
          </div>
        ) : (
          <Table striped bordered hover size="sm">
            <thead className="sticky-top bg-light">
              <tr>
                <th>Student</th>
                <th>Current Class</th>
                <th>Action</th>
                <th>Details</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {selectedStudents.map(student => {
                const decision = workflowData.studentDecisions[student.id] || {};
                const isValid = isDecisionValid(decision);
                
                return (
                  <tr key={student.id}>
                    <td>
                      <div className="fw-medium">
                        {student.firstName && student.lastName 
                          ? `${student.firstName} ${student.lastName}` 
                          : student.name}
                      </div>
                    </td>
                    <td>
                      <small className="text-muted">
                        {student.className}<br />
                        ({student.grade})
                      </small>
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        {['move', 'repeat', 'remove'].map(action => (
                          <Button
                            key={action}
                            variant={decision.action === action ? 'primary' : 'outline-secondary'}
                            onClick={() => handleActionChange(student.id, action)}
                            size="sm"
                          >
                            {action === 'move' && <FaArrowRight className="me-1" />}
                            {action === 'repeat' && <FaRedo className="me-1" />}
                            {action === 'remove' && <FaTrash className="me-1" />}
                            {action.charAt(0).toUpperCase() + action.slice(1)}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </td>
                    <td>
                      {decision.action === 'move' && (
                        <Form.Select
                          size="sm"
                          value={decision.targetClassId || ''}
                          onChange={(e) => updateStudentDecision(student.id, 'targetClassId', e.target.value)}
                        >
                          <option value="">Select Class</option>
                          {createdClasses.map(classItem => (
                            <option key={classItem.id} value={classItem.id}>
                              {classItem.name}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                      
                      {decision.action === 'repeat' && (
                        <div>
                          <Form.Select
                            size="sm"
                            value={decision.repeatYear || ''}
                            onChange={(e) => updateStudentDecision(student.id, 'repeatYear', e.target.value)}
                            className="mb-1"
                          >
                            {academicYears.map(year => (
                              <option key={year.value} value={year.value}>
                                {year.value}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control
                            size="sm"
                            as="textarea"
                            rows={2}
                            placeholder="Reason for repeating..."
                            value={decision.reason || ''}
                            onChange={(e) => updateStudentDecision(student.id, 'reason', e.target.value)}
                          />
                        </div>
                      )}
                      
                      {decision.action === 'remove' && (
                        <Form.Control
                          size="sm"
                          as="textarea"
                          rows={2}
                          placeholder="Reason for removal..."
                          value={decision.reason || ''}
                          onChange={(e) => updateStudentDecision(student.id, 'reason', e.target.value)}
                        />
                      )}
                    </td>
                    <td>
                      {isValid ? (
                        <Badge bg="success">
                          <FaCheck className="me-1" />
                          Ready
                        </Badge>
                      ) : decision.action ? (
                        <Badge bg="warning">
                          <FaExclamationTriangle className="me-1" />
                          Incomplete
                        </Badge>
                      ) : (
                        <Badge bg="secondary">Pending</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default StudentDecisionStep;